import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi } from "hooks/useApi";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useNotification } from "./useNotification";

type Props = {
  entityId: string;
  seasonId: string;
};

export const useDeleteSeasonTask = () => {
  const { formatMessage } = useIntl();
  const { sport, organizationId } = useParams();
  const queryClient = useQueryClient();
  const callApi = useApi();
  const { showSuccessNotification } = useNotification();

  return useMutation({
    mutationFn: ({ entityId, seasonId }: Props) => {
      return callApi(`/${sport}/o/${organizationId}/entities/${entityId}/seasons/${seasonId}/task`, {
        method: "DELETE",
        body: undefined,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["seasons", sport, organizationId],
      });
      showSuccessNotification(formatMessage({ id: "notification.task.deleted" }));
    },
  });
};

export const useSubmitSeasonTask = () => {
  const { formatMessage } = useIntl();
  const { sport, organizationId } = useParams();
  const queryClient = useQueryClient();
  const callApi = useApi();
  const { showSuccessNotification } = useNotification();

  return useMutation({
    mutationFn: ({ entityId, seasonId }: Props) => {
      return callApi(`/${sport}/o/${organizationId}/entities/${entityId}/seasons/${seasonId}/task/submit`, {
        method: "PATCH",
        body: {},
      });
    },
    onSuccess: (_, { entityId, seasonId }: Props) => {
      queryClient.invalidateQueries({
        queryKey: ["season", sport, organizationId, entityId, seasonId],
      });
      showSuccessNotification(formatMessage({ id: "notification.task.submitted" }));
    },
  });
};

export const useRecallSeasonTask = () => {
  const { formatMessage } = useIntl();
  const { sport, organizationId } = useParams();
  const queryClient = useQueryClient();
  const callApi = useApi();
  const { showSuccessNotification } = useNotification();

  return useMutation({
    mutationFn: ({ entityId, seasonId }: Props) => {
      return callApi(`/${sport}/o/${organizationId}/entities/${entityId}/seasons/${seasonId}/task/recall`, {
        method: "PATCH",
        body: {},
      });
    },
    onSuccess: (_, { entityId, seasonId }: Props) => {
      queryClient.invalidateQueries({
        queryKey: ["season", sport, organizationId, entityId, seasonId],
      });
      showSuccessNotification(formatMessage({ id: "notification.task.recalled" }));
    },
  });
};

export const useRejectSeasonTask = () => {
  const { formatMessage } = useIntl();
  const { sport, organizationId } = useParams();
  const queryClient = useQueryClient();
  const callApi = useApi();
  const { showSuccessNotification } = useNotification();

  return useMutation({
    mutationFn: ({ entityId, seasonId, data }: Props & { data: any }) => {
      return callApi(`/${sport}/o/${organizationId}/entities/${entityId}/seasons/${seasonId}/task/reject`, {
        method: "PATCH",
        body: data,
      });
    },
    onSuccess: (_, { entityId, seasonId }: Props & { data: any }) => {
      queryClient.invalidateQueries({
        queryKey: ["season", sport, organizationId, entityId, seasonId],
      });
      showSuccessNotification(formatMessage({ id: "notification.task.rejected" }));
    },
  });
};

export const useReviewSeasonTask = () => {
  const { formatMessage } = useIntl();
  const { sport, organizationId } = useParams();
  const queryClient = useQueryClient();
  const callApi = useApi();
  const { showSuccessNotification } = useNotification();

  return useMutation({
    mutationFn: ({ entityId, seasonId }: Props) => {
      return callApi(`/${sport}/o/${organizationId}/entities/${entityId}/seasons/${seasonId}/task/review`, {
        method: "PATCH",
        body: {},
      });
    },
    onSuccess: (_, { entityId, seasonId }: Props) => {
      queryClient.invalidateQueries({
        queryKey: ["season", sport, organizationId, entityId, seasonId],
      });
      showSuccessNotification(formatMessage({ id: "notification.task.reviewStarted" }));
    },
  });
};

export const useApproveSeasonTask = () => {
  const { formatMessage } = useIntl();
  const { sport, organizationId } = useParams();
  const queryClient = useQueryClient();
  const callApi = useApi();
  const { showSuccessNotification } = useNotification();

  return useMutation({
    mutationFn: ({ entityId, seasonId }: Props) => {
      return callApi(`/${sport}/o/${organizationId}/entities/${entityId}/seasons/${seasonId}/task/approve`, {
        method: "PATCH",
        body: {},
      });
    },
    onSuccess: (_, { entityId, seasonId }: Props) => {
      queryClient.invalidateQueries({
        queryKey: ["season", sport, organizationId, entityId, seasonId],
      });
      showSuccessNotification(formatMessage({ id: "notification.task.approved" }));
    },
  });
};

export const usePublishSeasonTask = () => {
  const { formatMessage } = useIntl();
  const { sport, organizationId } = useParams();
  const queryClient = useQueryClient();
  const callApi = useApi();
  const { showSuccessNotification } = useNotification();

  return useMutation({
    mutationFn: ({ entityId, seasonId }: Props) => {
      return callApi(`/${sport}/o/${organizationId}/entities/${entityId}/seasons/${seasonId}/task/publish`, {
        method: "PATCH",
        body: {},
      });
    },
    onSuccess: (_, { entityId, seasonId }: Props) => {
      queryClient.invalidateQueries({
        queryKey: ["season", sport, organizationId, entityId, seasonId],
      });
      showSuccessNotification(formatMessage({ id: "notification.task.publishingStarted" }));
    },
  });
};
